import { CURRENCIES_MAP, type Currency } from '../constants/index.ts'

export const getCurrencySymbol = (currency: Currency): string => {
  const symbol = CURRENCIES_MAP[currency]?.symbol

  if (!symbol) {
    throw new Error('Requested currency is not listed as valid currency')
  }

  return symbol
}

export const isZeroDecimal = (currency: Currency): boolean =>
  (CURRENCIES_MAP[currency] && CURRENCIES_MAP[currency].zeroDecimal) || false

export const normalizeAmount = ({
  amount,
  currency,
}: {
  amount: number
  currency: Currency
}): number => {
  if (isZeroDecimal(currency)) {
    return amount
  }

  return amount * 100
}

export const denormalizeAmount = ({
  amount,
  currency,
}: {
  amount: number
  currency: Currency
}): number => {
  if (isZeroDecimal(currency)) {
    return amount
  }

  return amount / 100
}

export const formatAmount = ({
  amount,
  currency,
  locale = 'en',
  space = ' ',
}: {
  amount: number
  currency: string
  locale?: string
  space?: string
}): string => {
  const fractionDigits = amount.toFixed(2).endsWith('.00') ? 0 : 2

  return (
    new Intl.NumberFormat(locale, {
      currency,
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
      style: 'currency',
    })
      .format(amount)
      // The following two replaces are needed to fix the space between the currency symbol and the amount
      // if the price begins with digit, place the space after the digit
      .replace(/^([\d,.]+)/u, `$1${space}`)
      // if the price ends with digit, place the space before the digit
      .replace(/([\d,.]+)$/u, `${space}$1`)
  )
}

export class MoneyCalculator {
  private amount: number
  private currency: Currency
  private isNormalized: boolean

  constructor({
    amount,
    currency,
    isNormalized = false,
  }: {
    amount: number
    currency: Currency
    isNormalized?: boolean
  }) {
    this.amount = amount
    this.currency = currency
    this.isNormalized = isNormalized
  }

  public denormalize(): this {
    if (!this.isNormalized) {
      return this
    }

    this.amount = denormalizeAmount({
      amount: this.amount,
      currency: this.currency,
    })

    this.isNormalized = false

    return this
  }

  public markup(percentage: number): this {
    this.amount /= 1 - percentage

    return this
  }

  public normalize(): this {
    if (this.isNormalized) {
      return this
    }

    this.amount = Number(
      normalizeAmount({
        // Making sure we only have 2 decimals after comma
        amount: Number(this.amount.toFixed(2)),
        currency: this.currency,
        // Making sure Javascript doesnt give us back a float number
        // 1172.64 * 100 = 117264.00000000001 💩
      }).toFixed(0),
    )

    this.isNormalized = true

    return this
  }

  public ceil(): this {
    this.amount = Math.ceil(this.amount)

    return this
  }

  public floor(): this {
    this.amount = Math.floor(this.amount)

    return this
  }

  public format(locale?: string, space?: string): string {
    return formatAmount({
      amount: this.amount,
      currency: this.currency,
      locale,
      space,
    })
  }

  public getAmount(): number {
    return this.amount
  }

  public convert({
    currency,
    exchangeRate,
  }: {
    exchangeRate: number
    currency: Currency
  }): this {
    if (this.currency === currency || this.amount === 0) {
      return this
    }

    const { isNormalized } = this

    if (isNormalized) {
      this.denormalize()
    }

    this.amount *= exchangeRate
    this.currency = currency

    if (isNormalized) {
      this.normalize()
    }

    return this
  }
}

export const ceilNormalizedAmount = ({
  amount,
  currency,
}: {
  amount: number
  currency: Currency
}): number => {
  const denormalizedAmount = denormalizeAmount({
    amount,
    currency,
  })

  return normalizeAmount({
    amount: Math.ceil(Math.floor(denormalizedAmount * 100) / 100),
    currency,
  })
}

export const floorNormalizedAmount = ({
  amount,
  currency,
}: {
  amount: number
  currency: Currency
}): number => {
  const denormalizedAmount = denormalizeAmount({
    amount,
    currency,
  })

  return normalizeAmount({
    amount: Math.floor(denormalizedAmount),
    currency,
  })
}
